import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { logError } from "@/lib/logger";

export function useDossier(id) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [related, setRelated] = useState([]);
  const [votes, setVotes] = useState(null);
  const [votedSide, setVotedSide] = useState(null);

  const loadTopic = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/topics/${id}`);
      setTopic(data);
    } catch (err) {
      logError("Failed to load dossier:", err);
      setError(true);
      const msg = err.response?.status === 503
        ? "This dossier is still being unsealed. Please try again in a moment."
        : "Could not unseal this dossier.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTopic();
  }, [loadTopic]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/bookmarks");
        setSaved(data.some((t) => t.id === id));
      } catch (err) {
        logError("Failed to load bookmark state:", err);
      }
    })();
  }, [user, id]);

  useEffect(() => {
    if (topic?.title) document.title = `${topic.title} | The Archive`;
    return () => {
      document.title = "The Archive | Controversy, Declassified";
    };
  }, [topic]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/topics/${id}/related?limit=4`);
        setRelated(data);
      } catch (err) {
        logError("Failed to load related dossiers:", err);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!topic) return;
    setVotes(topic.votes || { a: 0, b: 0 });
    setVotedSide(localStorage.getItem(`vote-${id}`));
  }, [topic, id]);

  const vote = useCallback(async (side) => {
    if (votedSide) return;
    try {
      const { data } = await api.post(`/topics/${id}/vote`, { side });
      setVotes(data.votes);
      setVotedSide(data.your_side || side);
      localStorage.setItem(`vote-${id}`, data.your_side || side);
    } catch (err) {
      logError("Vote failed:", err);
      toast.error("Couldn't record your vote");
    }
  }, [id, votedSide]);

  const [comments, setComments] = useState([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentSort, setCommentSort] = useState("new");

  const loadComments = useCallback(async (skip = 0, sort = "new") => {
    try {
      const { data } = await api.get(`/topics/${id}/comments?skip=${skip}&limit=20&sort=${sort}`);
      setCommentTotal(data.total);
      setComments((prev) => (skip === 0 ? data.comments : [...prev, ...data.comments]));
    } catch (err) {
      logError("Failed to load comments:", err);
    }
  }, [id]);

  useEffect(() => {
    if (id) loadComments(0, "new");
  }, [id, loadComments]);

  const loadMoreComments = useCallback(() => loadComments(comments.length, commentSort),
    [loadComments, comments.length, commentSort]);

  const changeCommentSort = useCallback((sort) => {
    setCommentSort(sort);
    loadComments(0, sort);
  }, [loadComments]);

  const addComment = useCallback(async (text) => {
    if (!user) {
      toast.info("Sign in to join the discussion.");
      navigate("/login");
      return false;
    }
    try {
      const { data } = await api.post(`/topics/${id}/comments`, { text });
      setComments((prev) => [data, ...prev]);
      setCommentTotal((t) => t + 1);
      return true;
    } catch (err) {
      logError("Add comment failed:", err);
      toast.error("Couldn't post your comment");
      return false;
    }
  }, [id, user, navigate]);

  const removeComment = useCallback(async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      logError("Delete comment failed:", err);
      toast.error("Couldn't delete comment");
    }
  }, []);

  const likeComment = useCallback(async (commentId) => {
    if (!user) {
      toast.info("Sign in to react.");
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.post(`/comments/${commentId}/like`);
      setComments((prev) => prev.map((c) =>
        c.id === commentId ? { ...c, likes: data.likes, liked: data.liked } : c));
    } catch (err) {
      logError("Like failed:", err);
    }
  }, [user, navigate]);

  const backgroundParas = useMemo(
    () => (topic?.background || "").split("\n").filter(Boolean), [topic]);
  const hiddenTruthParas = useMemo(
    () => (topic?.hidden_truth || "").split("\n").filter(Boolean), [topic]);

  const toggleBookmark = useCallback(async () => {
    if (!user) {
      toast.info("Sign in to save dossiers to your archive.");
      navigate("/login");
      return;
    }
    try {
      if (saved) {
        await api.delete(`/bookmarks/${id}`);
        setSaved(false);
        toast.success("Removed from saved");
      } else {
        await api.post(`/bookmarks/${id}`);
        setSaved(true);
        toast.success("Saved to your archive");
      }
    } catch (err) {
      logError("Bookmark action failed:", err);
      toast.error("Action failed");
    }
  }, [user, saved, id, navigate]);

  const shareDossier = useCallback(async () => {
    const shareUrl = `${API}/share/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `The Archive: ${topic?.title}`, text: topic?.teaser, url: shareUrl });
        return;
      }
    } catch (err) {
      return; // user dismissed the native share sheet
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Shareable dossier link copied to clipboard");
    } catch (err) {
      logError("Clipboard write failed:", err);
      toast.error("Couldn't copy link");
    }
  }, [id, topic]);

  const copyCard = useCallback(async () => {
    const cardUrl = `${API}/og/${id}`;
    try {
      const res = await fetch(cardUrl);
      const blob = await res.blob();
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
        toast.success("Share card image copied to clipboard");
        return;
      }
      throw new Error("clipboard unsupported");
    } catch (err) {
      const a = document.createElement("a");
      a.href = cardUrl;
      a.download = `the-archive-${id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Share card image downloaded");
    }
  }, [id]);

  const copyEmbed = useCallback(async () => {
    const snippet = `<iframe src="${API}/embed/${id}" width="100%" height="240" style="border:0;max-width:520px" loading="lazy" title="The Archive dossier"></iframe>`;
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Embed code copied — paste it into your site");
    } catch (err) {
      logError("Clipboard write failed:", err);
      toast.error("Couldn't copy embed code");
    }
  }, [id]);

  return {
    topic, loading, error, saved, backgroundParas, hiddenTruthParas,
    toggleBookmark, shareDossier, copyCard, copyEmbed, navigate, reload: loadTopic,
    related, votes, votedSide, vote,
    comments, commentTotal, commentSort, changeCommentSort,
    addComment, removeComment, likeComment, loadMoreComments, user,
  };
}
